import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, Area, ComposedChart
} from 'recharts';
import '../css/Energie.css';
import { Link } from 'react-router-dom';
import api from './api';
import { 
  calculatePredictedTotal, 
  isCurrentEnergyYear, 
  getLatestMonthWithData 
} from '../utils/energyCalculations';

const barColors = ['#3B82F6', '#F87171', '#10B981', '#facc15']; // Blue, red, green, yellow
const lineColors = ['#1E40AF', '#DC2626', '#059669', '#D97706']; // Darker versions for lines

const EnergyYearComparison = ({ setPredictionComparison }) => {
  const [data, setData] = useState([]);
  const [households, setHouseholds] = useState([]);
  const [selectedHouse, setSelectedHouse] = useState('');
  const [chartData, setChartData] = useState([]);
  const [cumulativeChartData, setCumulativeChartData] = useState([]); // New state for cumulative data
  const [totals, setTotals] = useState({});
  const [calculatedSums, setCalculatedSums] = useState({});
  const [deviceTotals, setDeviceTotals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [language, setLanguage] = useState('nl'); // Dutch by default
  const [lastImportDate, setLastImportDate] = useState(null); // 🆕 Add last import date state

  const [predictedTotal, setPredictedTotal] = useState(null);
  const [predictedFrom, setPredictedFrom] = useState(null);
  const [predictionMonthName, setPredictionMonthName] = useState('');
  const [currentYearKey, setCurrentYearKey] = useState('');
  
  // New state for energy year dropdown
  const [selectedDeviceEnergyYear, setSelectedDeviceEnergyYear] = useState('');
  const [availableEnergyYears, setAvailableEnergyYears] = useState([]);
  
  // Ref for the component to capture for PDF
  const componentRef = useRef();

  // Language translations
  const translations = {
    nl: {
      title: 'Energieverbruik per Maand',
      selectHouse: 'Selecteer Woning:',
      choose: '-- Kies --',
      exportCSV: 'Exporteer CSV',
      exportPDF: 'Exporteer PDF',
      loading: 'Gegevens laden...',
      monthlyUsage: 'Maandelijks Verbruik',
      cumulativeUsage: 'Cumulatief Verbruik gedurende Energiejaar',
      cumulativeDescription: 'Deze grafiek toont het lopend totaal van energieverbruik gedurende elk energiejaar, waardoor het makkelijker wordt om voortgang te vergelijken en seizoenspatronen te identificeren.',
      totalUsagePerDevice: 'Totaal Verbruik per Apparaat',
      energyYear: 'Energiejaar:',
      deviceName: 'Apparaatnaam',
      meterNumber: 'Meternummer',
      totalUsage: 'Totaal Verbruik',
      percentOfTotal: '% van Totaal',
      total: 'TOTAAL',
      showingDeviceBreakdown: 'Toont apparaat uitsplitsing voor energiejaar',
      july: 'juli',
      june: 'juni',
      calculatedFromMonths: 'Berekend uit maanden:',
      units: 'eenheden',
      yearOverYearChange: 'Jaar-op-jaar Verandering:',
      predictedTotal: 'Voorspeld Totaal',
      basedOn: 'Gebaseerd op',
      usedUpTo: 'gebruikt tot',
      stookseizoen: 'Stookseizoen',
      cumulative: 'Cumulatief',
      pdfTitle: 'Energieverbruik Rapport',
      apartment: 'Appartement:',
      period: 'Periode:',
      generated: 'Gegenereerd:',
      lastIstaImport: 'Laatste ISTA import:', // 🆕 Add translation
      months: {
        'Jul': 'Jul', 'Aug': 'Aug', 'Sep': 'Sep', 'Oct': 'Okt', 'Nov': 'Nov', 'Dec': 'Dec',
        'Jan': 'Jan', 'Feb': 'Feb', 'Mar': 'Mrt', 'Apr': 'Apr', 'May': 'Mei', 'Jun': 'Jun'
      }
    },
    en: {
      title: 'Energy Usage per Month',
      selectHouse: 'Select House:',
      choose: '-- Choose --',
      exportCSV: 'Export CSV',
      exportPDF: 'Export PDF',
      loading: 'Loading data...',
      monthlyUsage: 'Monthly Usage',
      cumulativeUsage: 'Cumulative Usage Throughout Energy Year',
      cumulativeDescription: 'This chart shows the running total of energy usage throughout each energy year, making it easier to compare progress and identify seasonal patterns.',
      totalUsagePerDevice: 'Total Usage per Device',
      energyYear: 'Energy Year:',
      deviceName: 'Device Name',
      meterNumber: 'Meter Number',
      totalUsage: 'Total Usage',
      percentOfTotal: '% of Total',
      total: 'TOTAL',
      showingDeviceBreakdown: 'Showing device breakdown for energy year',
      july: 'July',
      june: 'June',
      calculatedFromMonths: 'Calculated from months:',
      units: 'units',
      yearOverYearChange: 'Year-over-Year Change:',
      predictedTotal: 'Predicted Total',
      basedOn: 'Based on',
      usedUpTo: 'used up to',
      stookseizoen: 'Heating Season',
      cumulative: 'Cumulative',
      pdfTitle: 'Energy Usage Report',
      apartment: 'Apartment:',
      period: 'Period:',
      generated: 'Generated:',
      lastIstaImport: 'Last ISTA import:', // 🆕 Add translation
      months: {
        'Jul': 'Jul', 'Aug': 'Aug', 'Sep': 'Sep', 'Oct': 'Oct', 'Nov': 'Nov', 'Dec': 'Dec',
        'Jan': 'Jan', 'Feb': 'Feb', 'Mar': 'Mar', 'Apr': 'Apr', 'May': 'May', 'Jun': 'Jun'
      }
    }
  };

  const t = translations[language];

  // Current energy year determination
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // JS months are 0-indexed
  const currentYear = currentDate.getFullYear();
  
  // Energy years run from July to June
  // If current month is July or later, we're in year X to X+1, otherwise in year X-1 to X
  const currentEnergyYearStart = currentMonth >= 7 ? currentYear : currentYear - 1;
  const currentEnergyYearEnd = currentEnergyYearStart + 1;
  
  // Calculate the last two complete energy years before the current one
  const previousEnergyYearStart = currentEnergyYearStart - 1;
  const previousEnergyYearEnd = previousEnergyYearStart + 1;
  
  const olderEnergyYearStart = currentEnergyYearStart - 2;
  const olderEnergyYearEnd = olderEnergyYearStart + 1;
  
  // Define date range for data loading - we'll get 3 years of data
  const from = `${olderEnergyYearStart}-07-01`;
  const to = `${currentEnergyYearEnd}-06-30`;

  // 🆕 Add function to fetch last import date
  const fetchLastImportDate = async () => {
    try {
      const response = await api.get('/api/ista/last-date');
      if (response.data && response.data.lastDate) {
        setLastImportDate(response.data.lastDate);
        console.log('📅 Last ISTA import date:', response.data.lastDate);
      }
    } catch (error) {
      console.error('Error fetching last import date:', error);
      // Don't show error to user, just log it
    }
  };

  // Handle window resize to detect mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        // 🆕 Fetch last import date
        await fetchLastImportDate();
        
        const res = await api.get('/api/ista/compare-energy-years');
        
        // Debug log the raw data to help with troubleshooting
        console.log('Raw data from API:', res.data);
        
        // Ensure we have valid data
        if (res.data && Array.isArray(res.data)) {
          setData(res.data);
          
          // Filter out entries with unusual values that might be errors
          const validHouses = res.data
            .filter(d => d.homeNumber !== undefined && d.homeNumber !== null)
            // Include both numeric and special names like 'Toren'
            .filter(d => !isNaN(d.homeNumber) || typeof d.homeNumber === 'string')
            .map(d => d.homeNumber);
            
          setHouseholds(validHouses);

          // Process predictions for each apartment
          processApartmentPredictions(res.data, validHouses);
        } else {
          console.error('Unexpected data format received from API:', res.data);
          setError('Received unexpected data format from the server.');
        }
      } catch (err) {
        console.error('❌ Error loading comparison data:', err.message);
        setError('Failed to load energy comparison data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [setPredictionComparison]);

  // Process apartment predictions - FIXED VERSION with dynamic June calculation
const processApartmentPredictions = (rawData, validHouses) => {
  const predictions = rawData
    .filter(apt => validHouses.includes(apt.homeNumber))
    .map(apartment => {
      // Get all valid year keys (totalYYYY_YYYY)
      const yearKeys = Object.keys(apartment)
        .filter(k => k.match(/^total\d{4}_\d{4}$/))
        .sort();
      
      if (yearKeys.length < 2) {
        return {
          homeNumber: apartment.homeNumber,
          predicted: null,
          previous: null,
          diff: null,
          percent: 'N/A'
        };
      }
      
      // Get the latest and previous year keys
      const latestYearKey = yearKeys[yearKeys.length - 1]; 
      const prevYearKey = yearKeys[yearKeys.length - 2];
      
      // Get the monthly data for latest year
      const yearMatch = latestYearKey.match(/total(\d{4})_(\d{4})/);
      if (!yearMatch) return null;
      
      const yearStart = yearMatch[1];
      const yearEnd = yearMatch[2];
      const latestYearPattern = `${yearStart}-${yearEnd}`;
      
      // Get the monthly data from the apartment
      const latestMonthlyData = apartment[latestYearPattern] || {};
      
      // Calculate the usage so far this year
      const actualSoFar = Object.entries(latestMonthlyData)
        .reduce((sum, [_, val]) => sum + (val || 0), 0);
      
      // Get the latest month with data
      const latestMonth = getLatestMonthWithData(latestMonthlyData);
      if (!latestMonth) return null;
      
      // Format month name for display
      const monthName = dayjs(`2023-${latestMonth}-01`).format('MMMM');
      
      // Use the unified prediction function
      const predicted = calculatePredictedTotal(
        actualSoFar,
        latestMonth,
        lastImportDate,
        isCurrentEnergyYear(latestYearPattern)
      );
      
      // Get previous year total
      const previous = apartment[prevYearKey] || 0;
      
      // Calculate difference and percentage
      const diff = predicted !== null ? predicted - previous : null;
      const percent = (previous > 0 && diff !== null) ? 
        ((diff / previous) * 100).toFixed(1) : 'N/A';

      return {
        homeNumber: apartment.homeNumber,
        predicted,
        previous,
        diff,
        percent,
        monthName,
        actualSoFar,
        latestDataMonth: monthName // Add for debugging
      };
    })
    .filter(Boolean); // Remove any null entries

  // Set the predictions for parent component
  setPredictionComparison(predictions);
};

  // New function to create cumulative chart data
  const createCumulativeChartData = (monthlyChartData, yearPatterns) => {
    const cumulativeData = [];
    
    // Initialize cumulative counters for each year
    const cumulatives = {};
    yearPatterns.forEach(year => {
      cumulatives[year] = 0;
    });
    
    // Calculate cumulative values for each month
    monthlyChartData.forEach(monthData => {
      const cumulativePoint = { month: monthData.month };
      
      yearPatterns.forEach(year => {
        // Add this month's value to the cumulative total
        cumulatives[year] += (monthData[year] || 0);
        cumulativePoint[`${year}_cumulative`] = Math.round(cumulatives[year]);
      });
      
      cumulativeData.push(cumulativePoint);
    });
    
    return cumulativeData;
  };

  useEffect(() => {
    if (!selectedHouse) return;
    
    const selected = data.find(d => d.homeNumber === selectedHouse);
    if (!selected) {
      console.log(`No data found for selected house: ${selectedHouse}`);
      return;
    }

    console.log(`Processing data for apartment: ${selectedHouse}`, selected);

    // Get all available energy years for this apartment
    const energyYearPatterns = Object.keys(selected)
      .filter(key => key.match(/^[0-9]{4}-[0-9]{4}$/))
      .filter(yearPattern => {
        // Only show energy years from 2023-2024 onwards (when data format became consistent)
        const [startYear] = yearPattern.split('-').map(Number);
        return startYear >= 2023;
      })
      .sort();
    
    // Set available energy years for dropdown
    setAvailableEnergyYears(energyYearPatterns);
    
    // Set default selected energy year to the most recent one
    if (energyYearPatterns.length > 0 && !selectedDeviceEnergyYear) {
      const mostRecentYear = energyYearPatterns[energyYearPatterns.length - 1];
      setSelectedDeviceEnergyYear(mostRecentYear);
    }

    // Include all months but handle June specially
    const monthLabels = ['07', '08', '09', '10', '11', '12', '01', '02', '03', '04', '05', '06'];

    const formatChartData = () => {
      // Get all the year patterns (YYYY-YYYY)
      const yearPatterns = Object.keys(selected)
        .filter(key => key.match(/^[0-9]{4}-[0-9]{4}$/))
        .sort();
      
      // Filter year patterns to only include the last 2 complete years and current year
      const relevantYearPatterns = yearPatterns.filter(yearPattern => {
        const [start] = yearPattern.split('-').map(Number);
        return start >= olderEnergyYearStart && start <= currentEnergyYearStart;
      });
      
      console.log(`Found year patterns: ${relevantYearPatterns.join(', ')}`);
      
      // If no relevant year patterns found, return empty data
      if (relevantYearPatterns.length === 0) {
        console.log("No relevant year patterns found for chart data");
        return { monthlyData: [], yearPatterns: [] };
      }
      
      // Create formatted data for the chart
      const monthlyData = monthLabels.map(month => {
        const monthKey = month < '07' ? 
          `${parseInt(dayjs().format('YYYY'))}-${month}` : 
          `${parseInt(dayjs().format('YYYY'))-1}-${month}`;
        
        const point = { month: t.months[dayjs(monthKey, 'YYYY-MM').format('MMM')] || dayjs(monthKey, 'YYYY-MM').format('MMM') };
        
        relevantYearPatterns.forEach(year => {
          const monthlyData = selected[year] || {};
          const monthValue = Object.keys(monthlyData)
            .find(key => key.endsWith(`-${month}`));
            
          // Critical fix: For June (month 06), we need to filter out extremely high values
          // that might be yearly totals incorrectly included in the monthly data
          if (month === '06') {
            const value = monthValue ? monthlyData[monthValue] : 0;
            
            // If this is a normal apartment (not Toren)
            if (selectedHouse !== 'Toren') {
              // Check if the value seems like a yearly total (extremely high)
              // A typical month's usage would rarely be more than 25% of a year's total
              const yearKey = `total${year.replace('-', '_')}`;
              const yearTotal = selected[yearKey] || 1000; // Default to 1000 if no total available
              
              // If the June value is more than 25% of the year's total, it's likely a yearly total incorrectly included
              if (value > yearTotal * 0.25) {
                point[year] = 0; // Set to a more reasonable value
              } else {
                point[year] = value;
              }
            } else {
              // For Toren, we might need a different approach
              // Use a reasonable cap based on typical monthly patterns
              const reasonableCap = 150; // Based on typical monthly values in the chart
              point[year] = value > reasonableCap ? reasonableCap : value;
            }
          } else {
            // For all other months, use the value as is
            point[year] = monthValue ? monthlyData[monthValue] : 0;
          }
        });
        
        return point;
      });
      
      return { monthlyData, yearPatterns: relevantYearPatterns };
    };

    // Process the yearly totals
    const processYearlyTotals = () => {
      const newTotals = {};
      const newCalculatedSums = {};
      
      // Get all year patterns (YYYY-YYYY)  
      const yearPatterns = Object.keys(selected)
        .filter(key => key.match(/^[0-9]{4}-[0-9]{4}$/))
        .sort();
      
      // Filter year patterns to only include the last 2 complete years and current year
      const relevantYearPatterns = yearPatterns.filter(yearPattern => {
        const [start] = yearPattern.split('-').map(Number);
        return start >= olderEnergyYearStart && start <= currentEnergyYearStart;
      });
      
      console.log(`Processing yearly totals - Found patterns: ${relevantYearPatterns.join(', ')}`);
      
      // Process each year pattern
      relevantYearPatterns.forEach(year => {
        // Convert year pattern to total key format
        const totalKey = `total${year.replace('-', '_')}`;
        const calculatedKey = `calculated${year.replace('-', '_')}`;
        
        // Get the official total
        const totalValue = selected[totalKey];
        
        // Get the calculated sum - ensure we handle null/undefined values
        const calculatedValue = selected[calculatedKey] || 
            Object.values(selected[year] || {}).reduce((sum, val) => sum + (val || 0), 0);
        
        console.log(`Year ${year}: Total = ${totalValue}, Calculated = ${calculatedValue}`);
        
        // Store the values
        newTotals[year] = totalValue;
        newCalculatedSums[year] = calculatedValue; 
      });
      
      return { newTotals, newCalculatedSums };
    };

    const { newTotals, newCalculatedSums } = processYearlyTotals();
    const { monthlyData, yearPatterns } = formatChartData();
    
    if (monthlyData.length > 0) {
      setChartData(monthlyData);
      setTotals(newTotals);
      setCalculatedSums(newCalculatedSums);
      
      // Create cumulative chart data
      const cumulativeData = createCumulativeChartData(monthlyData, yearPatterns);
      setCumulativeChartData(cumulativeData);
      
      // Make predictions based on the latest data
      makePrediction(selected, newTotals);
    } else {
      console.log('No chart data could be generated');
      setChartData([]);
      setCumulativeChartData([]);
      setTotals({});
      setCalculatedSums({});
    }

    // Fetch device totals for the selected energy year
    if (selectedDeviceEnergyYear) {
      fetchDeviceTotals(selectedHouse, selectedDeviceEnergyYear);
    }
  }, [selectedHouse, data, olderEnergyYearStart, currentEnergyYearStart, selectedDeviceEnergyYear, lastImportDate]); // 🆕 Add lastImportDate dependency

  // Extract makePrediction function for clarity - FIXED VERSION with dynamic June
  const makePrediction = (selectedData, totalsData) => {
    // Base seasonality map for prediction
    const baseSeasonalityMap = {
      '07': 0.01, '08': 0.03, '09': 0.05, '10': 0.12,
      '11': 0.24, '12': 0.40, '01': 0.57, '02': 0.71,
      '03': 0.84, '04': 0.93, '05': 0.98, '06': 1.00
    };

    // Find the latest year data - limit to current energy year
    const yearPatterns = Object.keys(selectedData)
      .filter(key => key.match(/^[0-9]{4}-[0-9]{4}$/))
      .filter(yearPattern => {
        const [start] = yearPattern.split('-').map(Number);
        return start >= olderEnergyYearStart && start <= currentEnergyYearStart;
      })
      .sort();
    
    if (yearPatterns.length === 0) return;
    
    const latestYear = yearPatterns[yearPatterns.length - 1];
    setCurrentYearKey(latestYear);
    
    // Get the monthly data for this year
    const latestYearData = selectedData[latestYear] || {};
    
    // Calculate actual usage so far - ensure we handle null/undefined values
    const actualSoFar = Object.values(latestYearData).reduce((sum, val) => sum + (val || 0), 0);
    setPredictedFrom(actualSoFar);
    
    // Find the latest month with ANY data (including 0 usage)
    const allMonthKeys = Object.keys(latestYearData).sort();
    
    if (allMonthKeys.length === 0) return;
    
    // Find the latest month with data by converting to proper date format and sorting
    const formattedMonthKeys = allMonthKeys.map(monthKey => {
      const [year, month] = monthKey.split('-');
      return {
        key: monthKey,
        sortValue: parseInt(`${year}${month}`),
        month: month
      };
    }).sort((a, b) => b.sortValue - a.sortValue);
    
    if (formattedMonthKeys.length === 0) return;
    
    const latestMonth = formattedMonthKeys[0].month;
    const monthNames = language === 'nl' ? 
      ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'] :
      ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    setPredictionMonthName(monthNames[parseInt(latestMonth) - 1]);
    
    // 🔧 FIXED: Dynamic June calculation based on actual current date and last import date
    let seasonFraction;
    
    if (latestMonth === '06') {
      // For June, calculate dynamic seasonality based on actual date within June
      const currentRealDate = new Date();
      const currentRealYear = currentRealDate.getFullYear();
      const currentRealMonth = currentRealDate.getMonth() + 1; // 1-12
      const currentRealDay = currentRealDate.getDate();
      
      // Determine which energy year we're analyzing
      const [energyYearStart, energyYearEnd] = latestYear.split('-').map(Number);
      
      // Check if we're in the current energy year and in June
      const isCurrentEnergyYear = `${energyYearStart}-${energyYearEnd}` === `${currentEnergyYearStart}-${currentEnergyYearEnd}`;
      
      if (isCurrentEnergyYear && currentRealMonth === 6) {
        // We're in June of the current energy year, calculate based on how much of June has passed
        
        // 🆕 If we have lastImportDate, use that to determine how much of June we have data for
        let effectiveJuneDay = currentRealDay;
        
        if (lastImportDate) {
          const importDate = new Date(lastImportDate);
          const importYear = importDate.getFullYear();
          const importMonth = importDate.getMonth() + 1;
          const importDay = importDate.getDate();
          
          // If the import date is in June of this year, use that day instead
          if (importYear === currentRealYear && importMonth === 6) {
            effectiveJuneDay = Math.min(importDay, currentRealDay);
            console.log(`🔧 Prediction using ISTA import date for June calculation: Day ${effectiveJuneDay} of June`);
          }
        }
        
        // Calculate what percentage of June has passed (based on ISTA data)
        const daysInJune = 30;
        const daysPassed = Math.min(effectiveJuneDay, daysInJune);
        const juneProgress = daysPassed / daysInJune;
        
        // June represents the final 2% of the energy year (from 98% to 100%)
        const baseSeasonality = 0.98; // 98% complete by end of May
        const juneContribution = 0.02 * juneProgress; // June's 2% * progress through June
        seasonFraction = baseSeasonality + juneContribution;
        
        console.log(`🔧 June prediction for ${selectedHouse}: Day ${effectiveJuneDay}/${daysInJune} (${(juneProgress * 100).toFixed(1)}% through June), season fraction: ${(seasonFraction * 100).toFixed(1)}%`);
      } else {
        // Not current energy year or not current June, use full June value
        seasonFraction = 1.00;
      }
    } else {
      // For all other months, use static seasonality map
      seasonFraction = baseSeasonalityMap[latestMonth] || 1;
    }
    
    // Improved prediction logic
    if (seasonFraction >= 1.0) {
      // We're at or past June (100% of energy year), so prediction = actual usage
      setPredictedTotal(Math.round(actualSoFar));
    } else if (seasonFraction > 0) {
      // We're partway through the year, extrapolate based on season
      setPredictedTotal(Math.round(actualSoFar / seasonFraction));
    }
  };

  // Updated fetchDeviceTotals function to accept energy year parameter
  const fetchDeviceTotals = (homeNumber, energyYear = null) => {
    if (!homeNumber) return;
    
    // Use selected energy year or default to current
    const targetEnergyYear = energyYear || selectedDeviceEnergyYear || currentYearKey;
    
    if (!targetEnergyYear) {
      console.log('No energy year specified for device totals');
      return;
    }
    
    setIsLoading(true);
    
    // Parse the energy year to get start and end years
    const [startYear, endYear] = targetEnergyYear.split('-').map(Number);
    
    // Calculate date range for the selected energy year
    const deviceDataFrom = `${startYear}-07-01`;
    const deviceDataTo = `${endYear}-06-30`;

    console.log(`Fetching device data for ${homeNumber} for energy year ${targetEnergyYear} (${deviceDataFrom} to ${deviceDataTo})`);
    
    api.get(`/api/ista/weekly-device-usage`, 
      { 
        params: { 
          from: deviceDataFrom, 
          to: deviceDataTo, 
          homeNumber
        } 
      }
    )
    .then(res => {
      const deviceData = res.data;
      console.log('Device data received:', deviceData);
      setDeviceTotals(deviceData);
    })
    .catch(err => {
      console.error('❌ Failed to fetch device totals:', err);
      setError('Failed to load device usage data.');
    })
    .finally(() => {
      setIsLoading(false);
    });
  };

  // Handler for when energy year dropdown changes
  const handleDeviceEnergyYearChange = (event) => {
    const newEnergyYear = event.target.value;
    setSelectedDeviceEnergyYear(newEnergyYear);
    
    if (selectedHouse && newEnergyYear) {
      fetchDeviceTotals(selectedHouse, newEnergyYear);
    }
  };

  // Export functions
  const exportToCSV = () => {
    if (!selectedHouse || !selectedDeviceEnergyYear || deviceTotals.length === 0) {
      alert('Please select an apartment and ensure device data is loaded before exporting.');
      return;
    }

    // Prepare CSV data
    const csvData = [];
    
    // Add header info
    csvData.push(['Energy Usage Report']);
    csvData.push(['Apartment:', selectedHouse]);
    csvData.push(['Energy Year:', selectedDeviceEnergyYear]);
    csvData.push(['Period:', `July ${selectedDeviceEnergyYear.split('-')[0]} - June ${selectedDeviceEnergyYear.split('-')[1]}`]);
    csvData.push(['Export Date:', new Date().toLocaleDateString('nl-NL')]);
    csvData.push([]); // Empty row
    
    // Add yearly totals if available
    if (Object.keys(totals).length > 0) {
      csvData.push(['=== YEARLY TOTALS ===']);
      Object.keys(totals).sort().forEach(year => {
        csvData.push([`Total ${year}:`, `${formatNumber(totals[year])} units`]);
      });
      csvData.push([]); // Empty row
    }
    
    // Add monthly data if available
    if (chartData.length > 0) {
      csvData.push(['=== MONTHLY USAGE ===']);
      csvData.push(['Month', ...Object.keys(chartData[0]).filter(key => key !== 'month').map(year => `${year} (units)`)]);
      
      chartData.forEach(monthData => {
        const row = [monthData.month];
        Object.keys(monthData).filter(key => key !== 'month').forEach(year => {
          row.push(monthData[year] || 0);
        });
        csvData.push(row);
      });
      csvData.push([]); // Empty row
    }
    
    // Add device breakdown
    csvData.push(['=== DEVICE BREAKDOWN ===']);
    csvData.push(['Device Name', 'Meter Number', 'Total Usage (units)', '% of Total']);
    
    const totalUsage = deviceTotals.reduce((sum, d) => sum + d.usage, 0);
    deviceTotals.forEach(device => {
      const percentage = totalUsage > 0 ? ((device.usage / totalUsage) * 100).toFixed(1) : '0';
      csvData.push([
        device.deviceName,
        device.serialNr,
        device.usage,
        `${percentage}%`
      ]);
    });
    
    // Add total row
    csvData.push(['TOTAL', '', totalUsage, '100%']);
    
    // Convert to CSV string
    const csvContent = csvData.map(row => 
      row.map(cell => 
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(',')
    ).join('\n');
    
    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `energy-usage-${selectedHouse}-${selectedDeviceEnergyYear}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = async () => {
    if (!selectedHouse || !selectedDeviceEnergyYear) {
      alert(language === 'nl' ? 
        'Selecteer eerst een appartement en energiejaar voordat je naar PDF exporteert.' :
        'Please select an apartment and energy year before exporting to PDF.'
      );
      return;
    }

    try {
      // Dynamic import to avoid bundle size issues
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;
      
      const element = componentRef.current;
      
      // Hide export buttons temporarily
      const exportButtons = element.querySelectorAll('.export-buttons');
      exportButtons.forEach(btn => btn.style.display = 'none');
      
      // Create PDF with strategic page breaks
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      
      let currentY = margin;
      let pageNumber = 1;
      
      // 1. Create Header Section with proper encoding
      const createHeader = () => {
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        // Fix: Use proper text encoding and avoid emoji
        const title = language === 'nl' ? 'Energieverbruik Rapport' : 'Energy Usage Report';
        pdf.text(title, margin, currentY);
        currentY += 15;
        
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'normal');
        const apartmentLabel = language === 'nl' ? 'Appartement:' : 'Apartment:';
        const energyYearLabel = language === 'nl' ? 'Energiejaar:' : 'Energy Year:';
        const periodLabel = language === 'nl' ? 'Periode:' : 'Period:';
        const generatedLabel = language === 'nl' ? 'Gegenereerd:' : 'Generated:';
        const julyLabel = language === 'nl' ? 'juli' : 'July';
        const juneLabel = language === 'nl' ? 'juni' : 'June';
        
        pdf.text(`${apartmentLabel} ${selectedHouse}`, margin, currentY);
        currentY += 8;
        pdf.text(`${energyYearLabel} ${selectedDeviceEnergyYear}`, margin, currentY);
        currentY += 8;
        pdf.text(`${periodLabel} ${julyLabel} ${selectedDeviceEnergyYear.split('-')[0]} - ${juneLabel} ${selectedDeviceEnergyYear.split('-')[1]}`, margin, currentY);
        currentY += 8;
        const dateStr = language === 'nl' ? 
          new Date().toLocaleDateString('nl-NL') + ' ' + new Date().toLocaleTimeString('nl-NL') :
          new Date().toLocaleDateString('en-US') + ' ' + new Date().toLocaleTimeString('en-US');
        pdf.text(`${generatedLabel} ${dateStr}`, margin, currentY);
        currentY += 15;
        
        // Add horizontal line
        pdf.line(margin, currentY, pageWidth - margin, currentY);
        currentY += 10;
      };
      
      createHeader();
      
      // 2. Capture Monthly Chart
      const monthlyChart = element.querySelector('.chart-section-monthly');
      if (monthlyChart) {
        const chartCanvas = await html2canvas(monthlyChart, {
          scale: 1.2,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true
        });
        
        const chartImgWidth = contentWidth;
        const chartImgHeight = (chartCanvas.height * chartImgWidth) / chartCanvas.width;
        
        // Check if chart fits on current page
        if (currentY + chartImgHeight > pageHeight - margin - 20) {
          pdf.addPage();
          pageNumber++;
          currentY = margin + 20;
        }
        
        const chartImgData = chartCanvas.toDataURL('image/png', 0.9);
        pdf.addImage(chartImgData, 'PNG', margin, currentY, chartImgWidth, chartImgHeight);
        currentY += chartImgHeight + 15;
      }
      
      // 3. Capture Cumulative Chart
      const cumulativeChart = element.querySelector('.chart-section-cumulative');
      if (cumulativeChart) {
        const cumulativeCanvas = await html2canvas(cumulativeChart, {
          scale: 1.2,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true
        });
        
        const cumulativeImgWidth = contentWidth;
        const cumulativeImgHeight = (cumulativeCanvas.height * cumulativeImgWidth) / cumulativeCanvas.width;
        
        // Force page break before cumulative chart (requirement 1)
        pdf.addPage();
        pageNumber++;
        currentY = margin + 20;
        
        const cumulativeImgData = cumulativeCanvas.toDataURL('image/png', 0.9);
        pdf.addImage(cumulativeImgData, 'PNG', margin, currentY, cumulativeImgWidth, cumulativeImgHeight);
        currentY += cumulativeImgHeight + 15;
      }
      
      // 4. Capture Summary Cards
      const summaryCards = element.querySelector('.usage-summary');
      if (summaryCards) {
        const summaryCanvas = await html2canvas(summaryCards, {
          scale: 1.2,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true
        });
        
        const summaryImgWidth = contentWidth;
        const summaryImgHeight = (summaryCanvas.height * summaryImgWidth) / summaryCanvas.width;
        
        // Check if summary fits on current page
        if (currentY + summaryImgHeight > pageHeight - margin - 20) {
          pdf.addPage();
          pageNumber++;
          currentY = margin + 20;
        }
        
        const summaryImgData = summaryCanvas.toDataURL('image/png', 0.9);
        pdf.addImage(summaryImgData, 'PNG', margin, currentY, summaryImgWidth, summaryImgHeight);
        currentY += summaryImgHeight + 15;
      }
      
      // 5. Capture Device Table (if exists)
      const deviceTable = element.querySelector('.device-table-section');
      if (deviceTable) {
        const tableCanvas = await html2canvas(deviceTable, {
          scale: 1.2,
          backgroundColor: '#ffffff',
          useCORS: true,
          allowTaint: true
        });
        
        const tableImgWidth = contentWidth;
        const tableImgHeight = (tableCanvas.height * tableImgWidth) / tableCanvas.width;
        
        // Check if table fits on current page, if not create new page
        if (currentY + tableImgHeight > pageHeight - margin - 20) {
          pdf.addPage();
          pageNumber++;
          currentY = margin + 20;
        }
        
        const tableImgData = tableCanvas.toDataURL('image/png', 0.9);
        pdf.addImage(tableImgData, 'PNG', margin, currentY, tableImgWidth, tableImgHeight);
      }
      
      // Add footer to all pages
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        const footerText = language === 'nl' ? 
          `VVE Polderzicht - Energie Analyse - Pagina ${i} van ${totalPages}` :
          `VVE Polderzicht - Energy Analysis - Page ${i} of ${totalPages}`;
        pdf.text(footerText, pageWidth / 2, pageHeight - 10, { align: 'center' });
      }
      
      // Show export buttons again
      exportButtons.forEach(btn => btn.style.display = 'flex');
      
      // Save PDF with proper filename
      const timestamp = new Date().toISOString().slice(0, 10);
      const filename = language === 'nl' ? 
        `Energie-Rapport-${selectedHouse}-${selectedDeviceEnergyYear}-${timestamp}.pdf` :
        `Energy-Report-${selectedHouse}-${selectedDeviceEnergyYear}-${timestamp}.pdf`;
      pdf.save(filename);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      const errorMsg = language === 'nl' ? 
        'Fout bij genereren PDF. Probeer opnieuw of controleer je browserinstellingen.' :
        'Error generating PDF. Please try again or check your browser settings.';
      alert(errorMsg);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return 'N/A';
    return new Intl.NumberFormat('nl-NL').format(num);
  };

  const sortedYears = Object.keys(totals).sort();
  let yoyChange = null;
  if (sortedYears.length >= 2) {
    const latest = totals[sortedYears[sortedYears.length - 1]];
    const previous = totals[sortedYears[sortedYears.length - 2]];
    if (previous > 0) {
      yoyChange = (((latest - previous) / previous) * 100).toFixed(1);
    }
  }

  const previous = sortedYears.length >= 2 ? totals[sortedYears[sortedYears.length - 2]] : null;

  // Custom tooltip for cumulative chart
  const renderCumulativeTooltip = (props) => {
    if (!props.active || !props.payload || props.payload.length === 0) {
      return null;
    }

    const data = props.payload[0].payload;
    
    return (
      <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
        <p className="font-semibold mb-2">{data.month}</p>
        {props.payload.map((entry, index) => {
          const yearName = entry.dataKey.replace('_cumulative', '');
          return (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              <span className="font-medium">{t.stookseizoen} {yearName}:</span> {formatNumber(entry.value)} {t.units} ({t.cumulative.toLowerCase()})
            </p>
          );
        })}
      </div>
    );
  };

  // Helper function to render legend for mobile
  const renderMobileLegend = (dataKeys) => {
    if (!isMobile) return null;
    
    return (
      <div className="mobile-legend">
        {dataKeys.map((key, index) => (
          <div key={key} className="legend-item">
            <span 
              className="legend-color-box" 
              style={{ 
                backgroundColor: key.includes('_cumulative') 
                  ? lineColors[index % lineColors.length] 
                  : barColors[index % barColors.length] 
              }}
            ></span>
            <span className="legend-text">
              {key.includes('_cumulative') 
                ? `${t.stookseizoen} ${key.replace('_cumulative', '')} (${t.cumulative})`
                : `${t.stookseizoen} ${key}`
              }
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="energy-year-comparison-container" ref={componentRef}>
      <div className="export-header">
        <h2 className="component-title">{t.title}</h2>
        
        {/* Language Switcher */}
        <div className="language-switcher">
          <button 
            className={`lang-btn ${language === 'nl' ? 'active' : ''}`}
            onClick={() => setLanguage('nl')}
          >
            NL
          </button>
          <span className="lang-separator">|</span>
          <button 
            className={`lang-btn ${language === 'en' ? 'active' : ''}`}
            onClick={() => setLanguage('en')}
          >
            EN
          </button>
        </div>
        
        {/* Export Buttons with better spacing */}
        {selectedHouse && selectedDeviceEnergyYear && deviceTotals.length > 0 && (
          <div className="export-buttons">
            <button
              onClick={exportToCSV}
              className="export-btn export-csv"
            >
              {t.exportCSV}
            </button>
            <button
              onClick={exportToPDF}
              className="export-btn export-pdf"
            >
              {t.exportPDF}
            </button>
          </div>
        )}
      </div>   

      <div className="house-selector">
        <label className="selector-label">
          {t.selectHouse}
          <select 
            value={selectedHouse} 
            onChange={e => setSelectedHouse(e.target.value)}
            className="styled-select"
          >
            <option value="">{t.choose}</option>
            {households.map(h => (
              <option key={h} value={h}>{h}</option>
            ))}
          </select>
        </label>
      </div>

      {isLoading && <p className="loading-text">{t.loading}</p>}
      {error && <div className="error-message">{error}</div>}

      {chartData.length > 0 && (
        <>
          {/* Monthly Bar Chart */}
          <div className="chart-section chart-section-monthly">
            <h3 className="chart-title">{t.monthlyUsage}</h3>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    cursor={false} 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                  />
                  {!isMobile && <Legend />}
                  {Object.keys(chartData[0])
                    .filter(key => key !== 'month')
                    .map((year, i) => (
                      <Bar
                        key={year}
                        dataKey={year}
                        fill={barColors[i % barColors.length]}
                        name={`${t.stookseizoen} ${year}`}
                      />
                    ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {renderMobileLegend(Object.keys(chartData[0]).filter(key => key !== 'month'))}
          </div>

          {/* Cumulative Line Chart */}
          {cumulativeChartData.length > 0 && (
            <div className="chart-section chart-section-cumulative">
              <h3 className="chart-title">{t.cumulativeUsage}</h3>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
                  <LineChart data={cumulativeChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip content={renderCumulativeTooltip} />
                    {!isMobile && <Legend />}
                    {Object.keys(cumulativeChartData[0])
                      .filter(key => key !== 'month' && key.includes('_cumulative'))
                      .map((yearKey, i) => {
                        const year = yearKey.replace('_cumulative', '');
                        return (
                          <Line
                            key={yearKey}
                            type="monotone"
                            dataKey={yearKey}
                            stroke={lineColors[i % lineColors.length]}
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                            name={`${t.stookseizoen} ${year} (${t.cumulative})`}
                          />
                        );
                      })}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {renderMobileLegend(Object.keys(cumulativeChartData[0]).filter(key => key !== 'month' && key.includes('_cumulative')))}
              <p className="chart-description">
                {t.cumulativeDescription}
              </p>
            </div>
          )}

          <div className="usage-summary">
            {sortedYears.map(year => (
              <div key={year} className="usage-summary-card">
                <p className="card-label">Total {year}:</p>
                <p className="card-value">{formatNumber(totals[year])} {t.units}</p>
                <p className="card-subtext">{t.calculatedFromMonths} {formatNumber(calculatedSums[year])} {t.units}</p>
              </div>
            ))}
            
            {yoyChange !== null && (
              <div className={`usage-summary-card ${
                parseFloat(yoyChange) > 10 ? 'prediction-red' : 
                parseFloat(yoyChange) < -10 ? 'prediction-green' : 
                'prediction-orange'
              }`}>
                <p className="card-label">{t.yearOverYearChange}</p>
                <p className="card-value">{yoyChange}%</p>
              </div>
            )}
            
            {predictedTotal && (
              <div
                className={`usage-summary-card ${
                  predictedTotal > previous
                    ? 'prediction-red'
                    : predictedTotal < previous
                    ? 'prediction-green'
                    : Math.abs(predictedTotal - previous) / previous <= 0.05
                    ? 'prediction-orange'
                    : ''
                }`}
              >
                <p className="card-label">
                  🔮 {t.predictedTotal} {currentYearKey}:
                </p>
                <p className="card-value">{formatNumber(predictedTotal)} {t.units}</p>
                <p className="card-subtext">
                  {t.basedOn} {formatNumber(predictedFrom)} {t.units} {t.usedUpTo} {predictionMonthName}
                </p>
                {/* 🆕 Add last ISTA import date to prediction card */}
                {lastImportDate && (
                  <p className="card-subtext" style={{ 
                    fontSize: '11px', 
                    marginTop: '4px', 
                    color: '#666',
                    borderTop: '1px solid rgba(0,0,0,0.1)',
                    paddingTop: '4px'
                  }}>
                    📅 {t.lastIstaImport} {new Date(lastImportDate).toLocaleDateString(language === 'nl' ? 'nl-NL' : 'en-US', {
                      year: 'numeric',
                      month: 'long', 
                      day: 'numeric'
                    })}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {deviceTotals.length > 0 && (
        <div className="device-table-section">
          <div className="device-table-header">
            <h3 className="table-title">📋 {t.totalUsagePerDevice}</h3>
            
            {/* Energy Year Dropdown */}
            <div className="year-selector">
              <label className="year-label">
                {t.energyYear}
              </label>
              <select 
                value={selectedDeviceEnergyYear} 
                onChange={handleDeviceEnergyYearChange}
                className="styled-select"
              >
                {availableEnergyYears.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="table-wrapper">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>📌 {t.deviceName}</th>
                  <th>{t.meterNumber}</th>
                  <th>{t.totalUsage}</th>
                  <th>{t.percentOfTotal}</th>
                </tr>
              </thead>
              <tbody>
                {deviceTotals.map((device, i) => {
                  const totalUsage = deviceTotals.reduce((sum, d) => sum + d.usage, 0);
                  const percentage = totalUsage > 0 ? ((device.usage / totalUsage) * 100).toFixed(1) : '0';
                  return (
                    <tr key={i} className={i % 2 === 0 ? 'even-row' : 'odd-row'}>
                      <td>{device.deviceName}</td>
                      <td className="serial-number">{device.serialNr}</td>
                      <td className="usage-value">{formatNumber(device.usage)}</td>
                      <td className="percentage-value">{percentage}%</td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td colSpan={2}>{t.total}</td>
                  <td className="usage-value">
                    {formatNumber(deviceTotals.reduce((sum, d) => sum + d.usage, 0))}
                  </td>
                  <td className="percentage-value">100%</td>
                </tr>
              </tbody>
            </table>
          </div>
        
          
          <div className="table-info">
            <p>{t.showingDeviceBreakdown} {selectedDeviceEnergyYear} 
               ({t.july} {selectedDeviceEnergyYear.split('-')[0]} - {t.june} {selectedDeviceEnergyYear.split('-')[1]})
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnergyYearComparison;